'use client';

import { useRouter } from 'next/navigation';
import { hasInAppHistory } from '@/lib/nav-history';
import { useDismissTapMount } from './tap-mount-store';

/**
 * Back's rules, for the Backs that unwind one screen:
 * the back-only dock (`BackOnlyDock`, `src/app/items/[itemId]/section-dock.tsx`),
 * the error screens' own Back (`src/app/error-screens.tsx`) and the dock's
 * first slot on compact (`src/components/app-shell.tsx`, `shell-back.ts`). A
 * skeleton mounted by a tap stands over this surface, and Back dismisses it
 * and stays here (docs/LOADING.md §2, tool 3); otherwise Back pops the
 * navigation that opened the screen, or walks to `fallbackHref` when popping
 * would leave the app, replacing the current entry (`src/lib/nav-history.ts`
 * says why it never pushes). A null `fallbackHref` is a
 * dock home, which has nowhere to walk: there Back only dismisses or pops,
 * and the shell draws it disabled while it can do neither.
 */
export function useBack(fallbackHref: string | null): () => void {
  const router = useRouter();
  const dismissTapMount = useDismissTapMount();
  return () => {
    if (dismissTapMount()) return;
    if (hasInAppHistory()) router.back();
    // Replaced, never pushed, so two pages never bounce (src/lib/nav-history.ts).
    else if (fallbackHref !== null) router.replace(fallbackHref);
  };
}
